import React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { MetricCard } from "@/components/dashboard/MetricCard"
import { PerformanceChart } from "@/components/dashboard/SimpleChart"
import { formatCurrency, formatPercentage, cn } from "@/lib/utils"
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  PieChart, 
  BarChart3, 
  Activity,
  Download,
  Play,
  X
} from "lucide-react"

// Mock data - will be replaced with real API data
const portfolioData = {
  totalValue: 157255,
  availableCash: 62200,
  dailyPnL: 3350,
  netExposure: 95.1,
  grossExposure: 57.3,
  dailyPnLPercent: 2.18,
}

const positions = [
  { symbol: "AAPL", position: "Long", shares: 150, marketValue: 28350, unrealizedPnL: 1250, returnPercent: 4.6, strategy: "Strategy_001" },
  { symbol: "MSFT", position: "Long", shares: 85, marketValue: 32130, unrealizedPnL: -890, returnPercent: -2.7, strategy: "Strategy_002" },
  { symbol: "GOOGL", position: "Long", shares: 45, marketValue: 15675, unrealizedPnL: 2100, returnPercent: 15.5, strategy: "Strategy_003" },
  { symbol: "TSLA", position: "Short", shares: 75, marketValue: 18900, unrealizedPnL: 890, returnPercent: 4.9, strategy: "Strategy_004" },
]

const recentSignals = [
  { action: "BUY", symbol: "AMZN", strategy: "Strategy_020", timeAgo: "28m ago" },
  { action: "SELL", symbol: "AMZN", strategy: "Strategy_015", timeAgo: "30m ago" },
  { action: "BUY", symbol: "AAPL", strategy: "Strategy_021", timeAgo: "37m ago" },
  { action: "SELL", symbol: "AAPL", strategy: "Strategy_049", timeAgo: "49m ago" },
  { action: "BUY", symbol: "AAPL", strategy: "Strategy_006", timeAgo: "21m ago" },
]

const riskMetrics = [
  { label: "Portfolio Beta", value: "0.85", status: "Low" },
  { label: "Max Daily VaR (95%)", value: "$2,847", status: "Moderate" },
  { label: "Correlation to SPY", value: "0.32", status: "Low" },
  { label: "Current Drawdown", value: "-1.2%", status: "Normal" },
  { label: "Leverage Ratio", value: "1.15x", status: "Conservative" },
]

// Mock performance data
const performanceData = {
  timestamps: ["9:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "1:00", "1:30"],
  values: [153000, 154200, 153800, 155100, 156500, 157000, 157255, 156800, 157255],
}

export function ModernTradingHub() {
  return (
    <div className="space-y-6">
      {/* Header Section with Quick Actions */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Trading Hub</h1>
          <p className="text-muted-foreground">
            Live positions, execution signals, and portfolio performance
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button size="sm">
            <Activity className="mr-2 h-4 w-4" />
            1 Day Risk Report
          </Button>
          <Button variant="profit" size="sm">
            <Play className="mr-2 h-4 w-4" />
            Execute Signals
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Portfolio Value"
          value={portfolioData.totalValue}
          format="currency"
          change={portfolioData.dailyPnLPercent}
          changeLabel="today"
          variant="profit"
          icon={<DollarSign className="h-4 w-4" />}
        />
        <MetricCard
          title="Daily P&L"
          value={portfolioData.dailyPnL}
          format="currency"
          change={portfolioData.dailyPnLPercent}
          changeLabel="today"
          variant="profit"
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <MetricCard
          title="Net Exposure"
          value={`${portfolioData.netExposure}%`}
          subtitle="95.1% of portfolio"
          variant="neutral"
          icon={<PieChart className="h-4 w-4" />}
        />
        <MetricCard
          title="Available Cash"
          value={portfolioData.availableCash}
          format="currency"
          subtitle="Ready for deployment"
          variant="neutral"
          icon={<BarChart3 className="h-4 w-4" />}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        
        {/* Performance Chart - Spans 2 columns */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Live Performance</CardTitle>
              <CardDescription>Real-time paper trading results</CardDescription>
            </CardHeader>
            <CardContent>
              <PerformanceChart
                data={performanceData}
                height={300}
                color="#00D09C"
              />
            </CardContent>
          </Card>
        </div>

        {/* Risk Metrics */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Risk Metrics</CardTitle>
              <CardDescription>Portfolio risk assessment</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {riskMetrics.map((metric, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{metric.label}</p>
                      <p className="text-lg font-bold">{metric.value}</p>
                    </div>
                    <Badge 
                      variant={
                        metric.status === "Low" || metric.status === "Normal" || metric.status === "Conservative" 
                          ? "success" 
                          : metric.status === "Moderate" 
                            ? "warning" 
                            : "error"
                      }
                    >
                      {metric.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Positions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Current Positions</CardTitle>
          <CardDescription>Active trading positions and performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 text-sm font-medium text-muted-foreground">Symbol</th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">Position</th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">Market Value</th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">Unrealized P&L</th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">% Return</th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">Strategy</th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((position, index) => (
                  <tr key={index} className="border-b last:border-b-0">
                    <td className="py-4 font-medium">{position.symbol}</td>
                    <td className="py-4">
                      <Badge variant={position.position === "Long" ? "success" : "error"}>
                        {position.position} {position.shares}
                      </Badge>
                    </td>
                    <td className="py-4 font-medium">{formatCurrency(position.marketValue)}</td>
                    <td className={cn(
                      "py-4 font-medium",
                      position.unrealizedPnL > 0 ? "text-profit" : "text-loss"
                    )}>
                      {position.unrealizedPnL > 0 ? "+" : ""}{formatCurrency(position.unrealizedPnL)}
                    </td>
                    <td className={cn(
                      "py-4 font-medium",
                      position.returnPercent > 0 ? "text-profit" : "text-loss"
                    )}>
                      {position.returnPercent > 0 ? "+" : ""}{position.returnPercent}%
                    </td>
                    <td className="py-4 text-muted-foreground">{position.strategy}</td>
                    <td className="py-4">
                      <Button variant="outline" size="sm">
                        Close
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Signals */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Signals</CardTitle>
          <CardDescription>Latest trading signals from active strategies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentSignals.map((signal, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg border p-4">
                <div className="flex items-center gap-4">
                  <Badge variant={signal.action === "BUY" ? "success" : "error"}>
                    {signal.action}
                  </Badge>
                  <div>
                    <p className="font-medium">{signal.symbol}</p>
                    <p className="text-sm text-muted-foreground">{signal.strategy}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{signal.timeAgo}</span>
                  <Button variant="outline" size="sm">
                    Execute
                  </Button>
                  <Button variant="ghost" size="sm">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}